import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  IconButton,
  Menu,
  Tooltip,
  TextField,
  Stack,
  Toolbar,
  Paper,
  Divider
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Flag as FlagIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  CompareArrows as CompareArrowsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Task {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  duration: number;
  progress: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignedTo: Array<{
    user: string;
    role: string;
  }>;
  dependencies: string[];
  milestone: boolean;
  criticalPath: boolean;
  schedule: string;
  parent?: string;
  children?: Task[];
  resources?: Array<{
    resourceId: string;
    name: string;
    type: 'labor' | 'equipment' | 'material';
    units: number;
    cost: number;
  }>;
  baseline?: {
    startDate: string;
    endDate: string;
    duration: number;
    progress: number;
  };
  actualStart?: string;
  actualEnd?: string;
  remainingWork?: number;
  totalFloat?: number;
  freeFloat?: number;
}

interface Schedule {
  _id: string;
  name: string;
  description: string;
  project: string;
  startDate: string;
  endDate: string;
  status: string;
  ganttSettings?: {
    timeScale?: 'hour' | 'day' | 'week' | 'month' | 'quarter';
    showCriticalPath?: boolean;
    showBaseline?: boolean;
    colorScheme?: 'default' | 'status' | 'priority' | 'resource';
    // Frontend-only properties (not in backend model yet)
    showMilestones?: boolean;
    showProgress?: boolean;
    showDependencies?: boolean;
  };
}

// TaskForm Component
interface TaskFormProps {
  task: Task | null;
  onSubmit: (taskData: Partial<Task>) => void;
  onCancel: () => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ task, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: task?.name || '',
    description: task?.description || '',
    startDate: task?.startDate ? task.startDate.split('T')[0] : '',
    endDate: task?.endDate ? task.endDate.split('T')[0] : '',
    duration: task?.duration || 1,
    progress: task?.progress || 0,
    status: task?.status || 'not_started' as const,
    priority: task?.priority || 'medium' as const,
    milestone: task?.milestone || false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : '',
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : '',
      assignedTo: [],
      dependencies: [],
      criticalPath: false
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Task Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        
        <TextField
          fullWidth
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
        />
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            required
          />
          
          <TextField
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Box>
        
        <TextField
          label="Duration (days)"
          type="number"
          value={formData.duration}
          onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) || 1 })}
          inputProps={{ min: 1 }}
          required
        />
        
        <Box>
          <Typography gutterBottom>Progress: {formData.progress}%</Typography>
          <Slider
            value={formData.progress}
            onChange={(_, value) => setFormData({ ...formData, progress: value as number })}
            min={0}
            max={100}
            step={5}
            marks
            valueLabelDisplay="auto"
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => setFormData({ ...formData, status: e.target.value as Task['status'] })}
            >
              <MenuItem value="not_started">Not Started</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              label="Priority"
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as Task['priority'] })}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
        <FormControlLabel
          control={
            <Switch
              checked={formData.milestone}
              onChange={(e) => setFormData({ ...formData, milestone: e.target.checked })}
            />
          }
          label="Mark as Milestone"
        />
      </Stack>
      
      <DialogActions sx={{ px: 0, mt: 3 }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="contained">
          {task ? 'Update Task' : 'Create Task'}
        </Button>
      </DialogActions>
    </Box>
  );
};

const GanttChart: React.FC = () => {
  console.log('🏗️ COMPONENT: GanttChart component initializing', { timestamp: new Date().toISOString() });
  
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const ganttRef = useRef<HTMLDivElement>(null);
  
  console.log('📍 PARAMS: Current route params', { scheduleId, timestamp: new Date().toISOString() });
  
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [timeScale, setTimeScale] = useState<'day' | 'week' | 'month'>('week');
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const [showMilestones, setShowMilestones] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [showDependencies, setShowDependencies] = useState(true);
  const [showBaseline, setShowBaseline] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [viewMode, setViewMode] = useState<'gantt' | 'resource' | 'tracking'>('gantt');
  const [colorScheme, setColorScheme] = useState<'status' | 'priority' | 'resource' | 'critical'>('status');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [criticalPathTasks, setCriticalPathTasks] = useState<string[]>([]);
  const [resourceUtilization, setResourceUtilization] = useState<{[key: string]: number}>({});

  // Debug logging for component lifecycle
  useEffect(() => {
    console.log('🎬 COMPONENT: GanttChart mounted', { timestamp: new Date().toISOString() });
    return () => {
      console.log('🎬 COMPONENT: GanttChart unmounting', { timestamp: new Date().toISOString() });
    };
  }, []);

  // Debug logging for state changes
  useEffect(() => {
    console.log('📊 STATE CHANGE: Schedule state updated', { 
      hasSchedule: !!schedule,
      scheduleId: schedule?._id,
      scheduleName: schedule?.name,
      timestamp: new Date().toISOString() 
    });
  }, [schedule]);

  useEffect(() => {
    console.log('📋 STATE CHANGE: Tasks state updated', { 
      tasksCount: tasks.length,
      taskIds: tasks.map(t => t._id),
      timestamp: new Date().toISOString() 
    });
  }, [tasks]);

  useEffect(() => {
    console.log('📋 STATE CHANGE: Schedules list updated', { 
      schedulesCount: schedules.length,
      scheduleIds: schedules.map(s => s._id),
      timestamp: new Date().toISOString() 
    });
  }, [schedules]);

  useEffect(() => {
    console.log('⏳ STATE CHANGE: Loading state updated', { 
      loading,
      timestamp: new Date().toISOString() 
    });
  }, [loading]);

  const fetchSchedulesList = async () => {
    console.log('🚀 API CALL START: fetchSchedulesList', { timestamp: new Date().toISOString() });
    try {
      console.log('⏳ Setting loading to true for schedules list');
      setLoading(true);
      console.log('📡 Making API request to: /schedules');
      const response = await api.get('/schedules');
      console.log('✅ API CALL SUCCESS: fetchSchedulesList', { 
        dataCount: response.data.data?.length || 0, 
        data: response.data.data,
        timestamp: new Date().toISOString() 
      });
      setSchedules(response.data.data || []);
    } catch (err: any) {
      console.error('❌ API CALL ERROR: fetchSchedulesList', { 
        error: err.response?.data?.message || err.message,
        timestamp: new Date().toISOString() 
      });
      setError(err.response?.data?.message || 'Failed to fetch schedules');
    } finally {
      console.log('⏹️ Setting loading to false for schedules list');
      setLoading(false);
      console.log('🏁 API CALL END: fetchSchedulesList', { timestamp: new Date().toISOString() });
    }
  };

  const fetchScheduleData = useCallback(async () => {
    console.log('🚀 API CALL START: fetchScheduleData', { scheduleId, timestamp: new Date().toISOString() });
    try {
      console.log('⏳ Setting loading to true for schedule data');
      setLoading(true);
      console.log('📡 Making parallel API requests:', {
        scheduleUrl: `/schedules/${scheduleId}`,
        tasksUrl: `/tasks?schedule=${scheduleId}`
      });
      const scheduleResponse = await api.get(`/schedules/${scheduleId}?includeTasks=true`);
      
      console.log('✅ API CALL SUCCESS: fetchScheduleData', { 
        schedule: scheduleResponse.data.data,
        tasksCount: scheduleResponse.data.tasks?.length || 0,
        tasks: scheduleResponse.data.tasks,
        timestamp: new Date().toISOString() 
      });
      
      setSchedule(scheduleResponse.data.data);
      const fetchedTasks = scheduleResponse.data.tasks || [];
      setTasks(fetchedTasks);
      
      // Calculate critical path and resource utilization
      calculateCriticalPath(fetchedTasks);
      calculateResourceUtilization(fetchedTasks);
      
      // Apply saved settings
      const savedSchedule = scheduleResponse.data.data;
      if (savedSchedule && savedSchedule.ganttSettings) {
        console.log('⚙️ Applying saved Gantt settings:', savedSchedule.ganttSettings);
        setTimeScale(savedSchedule.ganttSettings.timeScale || 'week');
        setShowCriticalPath(savedSchedule.ganttSettings.showCriticalPath ?? true);
        // Use fallback values for properties that don't exist in backend model yet
        setShowMilestones(savedSchedule.ganttSettings.showMilestones ?? true);
        setShowProgress(savedSchedule.ganttSettings.showProgress ?? true);
        setShowDependencies(savedSchedule.ganttSettings.showDependencies ?? true);
      } else {
        console.log('⚙️ No ganttSettings found, using defaults');
        // Set default values when ganttSettings is missing
        setTimeScale('week');
        setShowCriticalPath(true);
        setShowMilestones(true);
        setShowProgress(true);
        setShowDependencies(true);
      }
    } catch (err: any) {
      console.error('❌ API CALL ERROR: fetchScheduleData', { 
        error: err.response?.data?.message || err.message,
        scheduleId,
        timestamp: new Date().toISOString() 
      });
      setError(err.response?.data?.message || 'Failed to fetch schedule data');
    } finally {
      console.log('⏹️ Setting loading to false for schedule data');
      setLoading(false);
      console.log('🏁 API CALL END: fetchScheduleData', { timestamp: new Date().toISOString() });
    }
  }, [scheduleId]);

  useEffect(() => {
    console.log('🔄 GanttChart useEffect triggered', { scheduleId, timestamp: new Date().toISOString() });
    if (scheduleId) {
      console.log('📊 Fetching schedule data for ID:', scheduleId);
      fetchScheduleData();
    } else {
      console.log('📋 Fetching schedules list');
      fetchSchedulesList();
    }
  }, [scheduleId, fetchScheduleData]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleSaveSettings = async () => {
    try {
      // Only save properties that exist in the backend model
      await api.put(`/schedules/${scheduleId}`, {
        ganttSettings: {
          timeScale,
          showCriticalPath,
          // Note: showMilestones, showProgress, showDependencies are frontend-only for now
          // They would need to be added to the backend Schedule model to persist
        }
      });
      setSettingsOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
    }
  };

  const handleExport = async (format: 'pdf' | 'png' | 'xlsx') => {
    try {
      const response = await api.get(`/schedules/${scheduleId}/export`, {
        params: { format },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gantt-chart.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export chart');
    }
  };

  const handleTaskCreate = async (taskData: Partial<Task>) => {
    try {
      // Generate a simple WBS code based on task count
      const wbsCode = `T${String(tasks.length + 1).padStart(3, '0')}`;
      
      const taskPayload = {
        name: taskData.name,
        description: taskData.description || '',
        scheduleId: scheduleId,
        wbs: wbsCode,
        startDate: {
          planned: taskData.startDate
        },
        endDate: {
          planned: taskData.endDate
        },
        duration: {
          planned: taskData.duration || 1
        },
        status: taskData.status || 'not_started',
        priority: taskData.priority || 'medium',
        assignedTo: taskData.assignedTo,
        milestone: taskData.milestone || false
      };
      
      console.log('🚀 Creating task with payload:', taskPayload);
      const response = await api.post('/tasks', taskPayload);
      console.log('✅ Task created successfully:', response.data);
      setTaskDialogOpen(false);
      fetchScheduleData();
    } catch (err: any) {
      console.error('❌ Task creation error:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create task');
    }
  };

  const handleTaskUpdate = async (taskId: string, taskData: Partial<Task>) => {
    try {
      await api.put(`/tasks/${taskId}`, taskData);
      setTaskDialogOpen(false);
      setEditingTask(null);
      fetchScheduleData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update task');
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    try {
      await api.delete(`/tasks/${taskId}`);
      fetchScheduleData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const getTaskColor = (task: Task) => {
    if (task.milestone) return '#ff9800';
    
    switch (colorScheme) {
      case 'critical':
        if (task.criticalPath) return '#f44336';
        if (task.totalFloat && task.totalFloat <= 1) return '#ff5722';
        return '#2196f3';
      
      case 'priority':
        return getPriorityColor(task.priority);
      
      case 'resource':
        if (!task.resources || task.resources.length === 0) return '#9e9e9e';
        const overAllocated = task.resources.some(r => (resourceUtilization[r.resourceId] || 0) > 100);
        return overAllocated ? '#f44336' : '#4caf50';
      
      case 'status':
      default:
        if (task.criticalPath && showCriticalPath) return '#f44336';
        if (task.status === 'completed') return '#4caf50';
        if (task.status === 'in_progress') return '#2196f3';
        if (task.status === 'on_hold') return '#ff5722';
        if (task.status === 'cancelled') return '#9e9e9e';
        return '#1976d2';
    }
  };

  const calculateCriticalPath = useCallback((tasks: Task[]) => {
    // Simple critical path calculation based on longest path
    const criticalTasks: string[] = [];
    
    // Find tasks with zero total float
    tasks.forEach(task => {
      if (task.totalFloat === 0 || task.criticalPath) {
        criticalTasks.push(task._id);
      }
    });
    
    setCriticalPathTasks(criticalTasks);
  }, []);

  const calculateResourceUtilization = useCallback((tasks: Task[]) => {
    const utilization: {[key: string]: number} = {};
    
    tasks.forEach(task => {
      if (task.resources) {
        task.resources.forEach(resource => {
          if (!utilization[resource.resourceId]) {
            utilization[resource.resourceId] = 0;
          }
          utilization[resource.resourceId] += resource.units;
        });
      }
    });
    
    setResourceUtilization(utilization);
  }, []);

  const getProgressVariance = (task: Task) => {
    if (!task.baseline) return 0;
    return task.progress - task.baseline.progress;
  };

  const getScheduleVariance = (task: Task) => {
    if (!task.baseline) return 0;
    const currentDuration = new Date(task.endDate).getTime() - new Date(task.startDate).getTime();
    const baselineDuration = new Date(task.baseline.endDate).getTime() - new Date(task.baseline.startDate).getTime();
    return (currentDuration - baselineDuration) / (1000 * 60 * 60 * 24); // in days
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const renderGanttChart = () => {
    console.log('📊 RENDER: renderGanttChart called', { 
      hasSchedule: !!schedule,
      tasksCount: tasks.length,
      scheduleId: schedule?._id,
      timestamp: new Date().toISOString() 
    });
    
    if (!schedule || tasks.length === 0) {
      console.log('⚠️ RENDER: No schedule or tasks found', { 
        hasSchedule: !!schedule,
        tasksCount: tasks.length,
        timestamp: new Date().toISOString() 
      });
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No tasks found for this schedule
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={() => setTaskDialogOpen(true)}
          >
            Add First Task
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ overflow: 'auto', height: '600px' }}>
        {/* Gantt Chart Header */}
        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ width: 300, p: 2, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Task Name
            </Typography>
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Timeline ({timeScale})
            </Typography>
          </Box>
        </Box>

        {/* Gantt Chart Rows */}
        {tasks.map((task, index) => (
          <Box
            key={task._id}
            sx={{
              display: 'flex',
              borderBottom: 1,
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
              cursor: 'pointer'
            }}
            onClick={() => {
              console.log('🖱️ CLICK: Task row clicked', { 
                taskId: task._id, 
                taskName: task.name,
                timestamp: new Date().toISOString() 
              });
              setSelectedTask(task);
            }}
          >
            {/* Task Info Column */}
            <Box sx={{ width: 300, p: 2, borderRight: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {task.milestone && <FlagIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                <Typography variant="body2" noWrap>
                  {task.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  label={task.status}
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
                <Chip
                  label={task.priority}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    bgcolor: getPriorityColor(task.priority),
                    color: 'white'
                  }}
                />
              </Box>
            </Box>

            {/* Timeline Column */}
            <Box sx={{ flex: 1, p: 2, position: 'relative' }}>
              <Box sx={{ position: 'relative' }}>
                {/* Baseline bar (if showing baseline) */}
                {showBaseline && task.baseline && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      width: `${(task.baseline.duration || task.duration) * 20}px`,
                      height: '8px',
                      top: '26px',
                      backgroundColor: 'rgba(158, 158, 158, 0.5)',
                      borderRadius: '2px',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                )}
                
                {/* Main task bar */}
                <Box
                  sx={{
                    height: 20,
                    bgcolor: getTaskColor(task),
                    borderRadius: 1,
                    position: 'relative',
                    width: `${task.duration * 20}px`, // Simplified width calculation
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    border: task.criticalPath ? '2px solid #d32f2f' : '1px solid rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  {task.milestone ? (
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '10px solid transparent',
                        borderRight: '10px solid transparent',
                        borderBottom: '20px solid #ff9800',
                        transform: 'rotate(45deg)'
                      }}
                    />
                  ) : (
                    <>
                      {showProgress && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${task.progress}%`,
                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                            borderRadius: 1
                          }}
                        />
                      )}
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem', position: 'relative', zIndex: 1 }}>
                        {task.name.length > 15 ? `${task.name.substring(0, 15)}...` : task.name}
                      </Typography>
                    </>
                  )}
                </Box>
                
                {/* Resource indicators */}
                {viewMode === 'resource' && task.resources && task.resources.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${task.duration * 20 + 10}px`,
                      top: '2px',
                      display: 'flex',
                      gap: '2px'
                    }}
                  >
                    {task.resources.slice(0, 3).map((resource, idx) => (
                      <Chip
                        key={idx}
                        label={resource.name}
                        size="small"
                        sx={{
                          height: '16px',
                          fontSize: '10px',
                          backgroundColor: (resourceUtilization[resource.resourceId] || 0) > 100 ? '#f44336' : '#4caf50',
                          color: 'white'
                        }}
                      />
                    ))}
                    {task.resources.length > 3 && (
                      <Chip
                        label={`+${task.resources.length - 3}`}
                        size="small"
                        sx={{ height: '16px', fontSize: '10px' }}
                      />
                    )}
                  </Box>
                )}
                
                {/* Progress variance indicator */}
                {viewMode === 'tracking' && task.baseline && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${task.duration * 20 + 10}px`,
                      top: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {getProgressVariance(task) !== 0 && (
                       <Chip
                         icon={getProgressVariance(task) > 0 ? <TrendingUpIcon /> : <WarningIcon />}
                         label={`${getProgressVariance(task) > 0 ? '+' : ''}${getProgressVariance(task).toFixed(1)}%`}
                         size="small"
                         color={getProgressVariance(task) > 0 ? 'success' : 'error'}
                         sx={{ height: '16px', fontSize: '10px' }}
                       />
                     )}
                     {getScheduleVariance(task) !== 0 && (
                       <Chip
                         icon={<ScheduleIcon />}
                         label={`${getScheduleVariance(task) > 0 ? '+' : ''}${getScheduleVariance(task).toFixed(1)}d`}
                         size="small"
                         color={getScheduleVariance(task) > 0 ? 'warning' : 'success'}
                         sx={{ height: '16px', fontSize: '10px' }}
                       />
                     )}
                  </Box>
                )}
              </Box>

              {/* Dependencies */}
              {showDependencies && task.dependencies.length > 0 && (
                <Box sx={{ position: 'absolute', top: -5, left: -10 }}>
                  <LinkIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    console.log('⏳ LOADING STATE: Gantt chart is loading', { 
      scheduleId, 
      hasSchedule: !!schedule,
      tasksCount: tasks.length,
      timestamp: new Date().toISOString() 
    });
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading Gantt chart...</Typography>
      </Box>
    );
  }

  // Show schedule selection if no scheduleId is provided
  if (!scheduleId) {
    console.log('📋 RENDER STATE: Showing schedule selection', { 
      schedulesCount: schedules.length,
      schedules: schedules.map(s => ({ id: s._id, name: s.name })),
      timestamp: new Date().toISOString() 
    });
    return (
      <Box sx={{ p: 3 }}>        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Gantt Chart
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Select a schedule to view its Gantt chart
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/project-planning/schedules/new')}
            >
              New Schedule
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {schedules.length === 0 ? (
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Schedules Found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Create a project schedule first to view Gantt charts
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/project-planning/schedules/new')}
                startIcon={<AddIcon />}
              >
                Create New Schedule
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/project-planning')}
              >
                Go to Project Planning
              </Button>
            </Box>
          </Card>
        ) : (
          <Grid container spacing={3}>
              {schedules.map((sched) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={sched._id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => {
                    console.log('🖱️ CLICK: Schedule card clicked', { 
                      scheduleId: sched._id, 
                      scheduleName: sched.name,
                      navigatingTo: `/project-planning/gantt/${sched._id}`,
                      timestamp: new Date().toISOString() 
                    });
                    navigate(`/project-planning/gantt/${sched._id}`);
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" component="h2">
                        {sched.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {sched.description || 'No description available'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        label={sched.status} 
                        size="small" 
                        color={sched.status === 'active' ? 'success' : 'default'}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {new Date(sched.startDate).toLocaleDateString()} - {new Date(sched.endDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  console.log('🎨 RENDER STATE: Rendering main Gantt chart view', { 
    scheduleId,
    hasSchedule: !!schedule,
    scheduleName: schedule?.name,
    tasksCount: tasks.length,
    loading,
    error: !!error,
    timestamp: new Date().toISOString() 
  });
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Gantt Chart
          </Typography>
          {schedule && (
            <Typography variant="subtitle1" color="textSecondary">
              {schedule.name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/project-planning/schedules/new')}
          >
            New Schedule
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setTaskDialogOpen(true)}
          >
            Add Task
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Toolbar */}
      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2 }}>
          <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 50}>
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2">
            {zoomLevel}%
          </Typography>
          <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 200}>
            <ZoomInIcon />
          </IconButton>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>View Mode</InputLabel>
            <Select
              value={viewMode}
              label="View Mode"
              onChange={(e) => setViewMode(e.target.value as 'gantt' | 'resource' | 'tracking')}
            >
              <MenuItem value="gantt">Gantt Chart</MenuItem>
              <MenuItem value="resource">Resource View</MenuItem>
              <MenuItem value="tracking">Progress Tracking</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Scale</InputLabel>
            <Select
              value={timeScale}
              label="Time Scale"
              onChange={(e) => setTimeScale(e.target.value as 'day' | 'week' | 'month')}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Color Scheme</InputLabel>
            <Select
              value={colorScheme}
              label="Color Scheme"
              onChange={(e) => setColorScheme(e.target.value as 'status' | 'priority' | 'resource' | 'critical')}
            >
              <MenuItem value="status">By Status</MenuItem>
              <MenuItem value="priority">By Priority</MenuItem>
              <MenuItem value="resource">By Resource</MenuItem>
              <MenuItem value="critical">Critical Path</MenuItem>
            </Select>
          </FormControl>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <IconButton onClick={() => handleExport('pdf')}>
            <FileDownloadIcon />
          </IconButton>
          <IconButton onClick={() => handleExport('png')}>
            <PrintIcon />
          </IconButton>
          <IconButton onClick={() => window.print()}>
            <FullscreenIcon />
          </IconButton>
        </Toolbar>
      </Paper>

      {/* Gantt Chart */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box ref={ganttRef} sx={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' }}>
            {renderGanttChart()}
          </Box>
        </CardContent>
      </Card>

      {/* Task Details Panel */}
      {selectedTask && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Task Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Name</Typography>
                <Typography variant="body2" gutterBottom>{selectedTask.name}</Typography>
                
                <Typography variant="subtitle2">Description</Typography>
                <Typography variant="body2" gutterBottom>{selectedTask.description}</Typography>
                
                <Typography variant="subtitle2">Status</Typography>
                <Chip label={selectedTask.status} size="small" sx={{ mb: 2 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2">Duration</Typography>
                <Typography variant="body2" gutterBottom>{selectedTask.duration} days</Typography>
                
                <Typography variant="subtitle2">Progress</Typography>
                <Typography variant="body2" gutterBottom>{selectedTask.progress}%</Typography>
                
                <Typography variant="subtitle2">Priority</Typography>
                <Chip
                  label={selectedTask.priority}
                  size="small"
                  sx={{
                    bgcolor: getPriorityColor(selectedTask.priority),
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setEditingTask(selectedTask);
                  setTaskDialogOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleTaskDelete(selectedTask._id)}
              >
                Delete
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gantt Chart Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" gutterBottom>
              Display Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showCriticalPath}
                  onChange={(e) => setShowCriticalPath(e.target.checked)}
                />
              }
              label="Show Critical Path"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showMilestones}
                  onChange={(e) => setShowMilestones(e.target.checked)}
                />
              }
              label="Show Milestones"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showProgress}
                  onChange={(e) => setShowProgress(e.target.checked)}
                />
              }
              label="Show Progress"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showDependencies}
                  onChange={(e) => setShowDependencies(e.target.checked)}
                />
              }
              label="Show Dependencies"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showBaseline}
                  onChange={(e) => setShowBaseline(e.target.checked)}
                />
              }
              label="Show Baseline Comparison"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showResources}
                  onChange={(e) => setShowResources(e.target.checked)}
                />
              }
              label="Show Resource Information"
            />
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              View Configuration
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                label="View Mode"
                onChange={(e) => setViewMode(e.target.value as 'gantt' | 'resource' | 'tracking')}
              >
                <MenuItem value="gantt">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShowChartIcon />
                    Gantt Chart
                  </Box>
                </MenuItem>
                <MenuItem value="resource">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Resource View
                  </Box>
                </MenuItem>
                <MenuItem value="tracking">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon />
                    Progress Tracking
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Color Scheme</InputLabel>
              <Select
                value={colorScheme}
                label="Color Scheme"
                onChange={(e) => setColorScheme(e.target.value as 'status' | 'priority' | 'resource' | 'critical')}
              >
                <MenuItem value="status">By Status</MenuItem>
                <MenuItem value="priority">By Priority</MenuItem>
                <MenuItem value="resource">By Resource Allocation</MenuItem>
                <MenuItem value="critical">Critical Path Focus</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => {
        setTaskDialogOpen(false);
        setEditingTask(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <TaskForm
            task={editingTask}
            onSubmit={(taskData) => {
              if (editingTask) {
                handleTaskUpdate(editingTask._id, taskData);
              } else {
                handleTaskCreate(taskData);
              }
            }}
            onCancel={() => {
              setTaskDialogOpen(false);
              setEditingTask(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GanttChart;